/**
 * Client for the generic synonym HITL API (/administration/synonyms/* on tri-server, root-admin only).
 * Mirrors LegalAdministrationAPI: authenticatedFetch + processResponse, GET/POST helpers.
 */
import { configuration } from "./Configuration";
import { ApplicationError, UnauthorizedError } from "./general/Errors";
import { authenticatedFetch } from "./JWTAuthService";

export type SynonymStatus = "proposed" | "approved" | "rejected";
export interface SynonymEntry {
  term: string;
  status: SynonymStatus;
  source: "llm" | "human";
  kind?: string;
  rationale?: string;
  editedBy?: string | null;
  editedAt?: string | null;
}
export interface SynonymRow {
  key: string;
  label: string;
  scope: string | null;
  context: Record<string, any>;
  synonyms: SynonymEntry[];
}
export interface SynonymCollection {
  collection: string;
  keyField: string;
  labelField: string;
  scopeField: string | null;
}
export type ReviewOp = "add" | "approve" | "reject" | "remove" | "edit";

const processResponse = async (response: Response) => {
  if (response.ok) return response.json();
  if (response.status === 401) throw new UnauthorizedError();
  let errorMessage = `${response.status} ${response.statusText}`;
  try {
    const errorData = await response.json();
    errorMessage = errorData.message || errorData.error || errorMessage;
  } catch {
    // body not JSON
  }
  throw new ApplicationError(errorMessage);
};

export const useSynonymsAPI = () => {
  const baseUrl = `${configuration.serverUrl}/administration/synonyms`;

  const get = async (path: string, params?: Record<string, any>) => {
    const query = params
      ? `?${new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null && v !== "")
            .map(([k, v]) => [k, String(v)]),
        ).toString()}`
      : "";
    const response = await authenticatedFetch(`${baseUrl}${path}${query}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    return processResponse(response);
  };

  const send = async (method: "POST", path: string, body: any) => {
    const response = await authenticatedFetch(`${baseUrl}${path}`, {
      method,
      body: JSON.stringify(body),
      headers: { "Content-Type": "application/json" },
    });
    return processResponse(response);
  };

  const enc = (c: string) => encodeURIComponent(c);
  return {
    getCollections: (): Promise<{ collections: SynonymCollection[] }> => get("/collections"),
    getEntities: (
      collection: string,
      params: { scope?: string; search?: string; limit?: number; offset?: number } = {},
    ): Promise<{ collection: string; total: number; rows: SynonymRow[] }> => get(`/${enc(collection)}`, params),
    review: (
      collection: string,
      body: { key: string; op: ReviewOp; term: string; newTerm?: string; kind?: string },
    ): Promise<{ entity: SynonymRow }> => send("POST", `/${enc(collection)}/review`, body),
    generate: (
      collection: string,
      body: { scope?: string; limit?: number } = {},
    ): Promise<{ objectsUpdated: number; proposalsAdded: number; shared: any[]; ambiguous: Record<string, string[]> }> =>
      send("POST", `/${enc(collection)}/generate`, body),
  };
};
